import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import { useApiCall } from 'hooks/use-api-call';
import { AdminApi, AdminInvitation } from 'api';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Section from 'ui/atoms/section';
import useGoTo from 'hooks/use-go-to';
import { ISSUER_ROUTES } from 'apps/issuer/pages/routes.config';
import BoxedIssuerInvestmentInvitation from 'apps/issuer/pages/product-details/invitations/invitation-details/boxed-invitation';
import Link from 'core/routing/link';
import InvitationInvestment from 'apps/issuer/pages/product-details/invitations/invitation-details/invitation-investment';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Header from 'src/ui/atoms/header';

const InvitationDetailsPage = (props: { invitationId: string }) => {
  const { invitationId } = props;

  const { withApi, makeAuthenticatedApi } = useApiCall(true);

  const [invitationDetails, setInvitationDetails] = useState<AdminInvitation & { name?: string }>();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const loadData = useCallback(() => {
    withApi(async () => {
      if (!invitationId) {
        return;
      }
      withApi(async () => {
        const invitationDetailsData = await api.adminInvitationsRetrieve({
          id: invitationId,
        });
        setInvitationDetails(invitationDetailsData);
      });
    });
  }, [withApi, api, invitationId]);

  const goToProductDetails = useGoTo(ISSUER_ROUTES.productDetails);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!invitationDetails) return null;

  return (
    <WideContent
      title={
        <Translate name={'issuerProductDetails.invitations.invitationDetails.title'} args={[invitationDetails.name]} />
      }
      titleSpacing="larger"
    >
      <BoxedIssuerInvestmentInvitation
        key={invitationDetails.id}
        issuersCanManage
        onDeactivated={goToProductDetails}
        {...invitationDetails}
      />
      <Header size="small" spacing="medium">
        <Translate name={'issuerProductDetails.invitations.invitationDetails.investments.title'} />
      </Header>
      <InvitationInvestment investmentId={invitationDetails.investment} />
      <Section>
        <ActionButtons align="start">
          <Link icon="arrow-left" to={`/dashboard/product/${invitationDetails.token.id}`}>
            <Translate as="span" name="issuerProductDetails.campaigns.campaignDetails.backToProductDetails" />
          </Link>
        </ActionButtons>
      </Section>
    </WideContent>
  );
};

export default WithAuthenticatedPage(InvitationDetailsPage, AppType.ISSUER);
